/**
 * InDeep-Net
 * Copyright (C) 2025
 * Authors: Fabien MAREUIL
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published
 * by the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */
import React from 'react';
import axios from "axios";
import { PluginCommands } from 'molstar/lib/mol-plugin/commands';
import { Color } from 'molstar/lib/mol-util/color';
import { loadStructuretoMolstar } from './molstar_structure';
import { LoadResultTrajectorytoMolstar } from './molstar_trajectory';
import { loadMRCfile } from './molstar_mrc';
import { JobMessage, WarningMessage, SetHrefMessage } from '../Utils.js';
import { compileIdListSelection } from 'molstar/lib/mol-script/util/id-list'
import { updateIsDetected, updateJobID } from "./HookStateUpdate.jsx";
import { DefaultPluginUISpec } from 'molstar/lib/mol-plugin-ui/spec';
import { createPluginUI } from 'molstar/lib/mol-plugin-ui/index';
import { PluginConfig } from 'molstar/lib/mol-plugin/config';


async function loadJobMolstar(plugin, job_data) {
  const hrefexport = document.getElementById('exporthref');
  hrefexport.href = `/api/export/${job_data.task_id}`;
  if (hrefexport.classList.contains('disabled')) {
    hrefexport.classList.remove('disabled')
  };

  if (job_data.trajectory) {
    var structure = job_data.resultfiles.find(x => x.type === 'pdb');
    var trajectory = job_data.resultfiles.find(x => x.type === 'dcd');
    await LoadResultTrajectorytoMolstar(plugin, structure.file, structure.type, job_data.structure, trajectory.file, trajectory.type, job_data.trajectory);
    const volume = document.getElementById('volume-range');
    volume.setAttribute('value', job_data.volume_pocket);
    const bubblePosition = ((job_data.volume_pocket - volume.min) / (volume.max - volume.min)) * 100;
    const bubbleElement = document.getElementById('bubbleposition');
    bubbleElement.style.left = `calc(${bubblePosition}%)`;
    bubbleElement.textContent = job_data.volume_pocket
  } else {
    await loadStructuretoMolstar(plugin, job_data.structure_path, job_data.structure_format, job_data.structure)
    for (var i = 0; i < job_data.resultfiles.length; i++) {
      let obj = job_data.resultfiles[i];
      try {
        await axios({
          method: "get",
          url: obj.file,
          responseType: 'blob',
        }).then(
          function (response) {
            let mrcblobfile = new File([response.data], obj.file.substring(obj.file.lastIndexOf('/') + 1));
            loadMRCfile(plugin, mrcblobfile);
          }
        );
      } catch (error) {
        WarningMessage(`File ${obj.file} on indeep, ${error}`)
      }
    }
  }
}

export const PreLoad = async (plugin, job_id) => {
  try {
    const response = await axios({
      method: "get",
      url: `/api/job?job=${job_id}`,
      responseType: 'json',
    });

    const { status, log_message: log, err_message: error_log, job_parent } = response.data;
    updateJobID(job_id);
    let message;
    const colorstatus = {
      'RUNNING': 'yellow',
      'INIT': 'grey',
      'READY': 'blue',
      'PENDING': 'blue',
      'SUCCESS': 'green',
      'KILLED': 'red',
      'ERROR': 'red',
    };
    
    if (['RUNNING', 'INIT', 'READY', 'PENDING'].includes(status)) {
      message = `Your indeep job is in ${status} status and not yet finished, please come back later.`;
    } else if (['ERROR', 'KILLED'].includes(status)) {
      message = `Your indeep job is in ${status} status, please check log error information.
log infos: 
  ${log}
error infos:
  ${error_log}`;
    } else {
      message = `Your indeep job is in ${status} status. 
log infos: 
  ${log}`;
      
      let data = response.data;
      if (job_parent) {
        try {
          const parentResponse = await axios({
            method: "get",
            url: `/api/job/${job_parent}`,
            responseType: 'json',
          });
          data = parentResponse.data;
        } catch (error) {
          WarningMessage(`${error.response?.data?.status || "Error fetching parent job"}`);
        }
      }

      loadJobMolstar(plugin, data);
    }
    JobMessage(message, colorstatus[status]);
    updateIsDetected(true);
    const url = `${window.location.href.split('?')[0]}?job=${job_id}`;
    SetHrefMessage(url);
  } catch (error) {
    WarningMessage(`${error.response?.data?.status || "Unexpected error occurred"}`);
  }
};


export const setBackgroundwithTheme = (plugin) => {
  // if set via local storage previously
  if (localStorage.getItem('color-theme')) {
    if (localStorage.getItem('color-theme') === 'light') {
      PluginCommands.Canvas3D.SetSettings(plugin, { settings: function (props) { props.renderer.backgroundColor = Color(0xFFFFFF); } });
    } else {
      PluginCommands.Canvas3D.SetSettings(plugin, { settings: function (props) { props.renderer.backgroundColor = Color(0x000000); } });
    }
    // if NOT set via local storage previously
  } else {
    if (document.documentElement.classList.contains('dark')) {
      PluginCommands.Canvas3D.SetSettings(plugin, { settings: function (props) { props.renderer.backgroundColor = Color(0xFFFFFF); } });
    } else {
      PluginCommands.Canvas3D.SetSettings(plugin, { settings: function (props) { props.renderer.backgroundColor = Color(0x000000); } });
    }
  }
}

export const toggleControlPanel = (buttoncontrolpanel) => {
  buttoncontrolpanel.addEventListener('click', function () {
    let divapp = document.getElementById('app')
    if (divapp.classList.contains('h-molstar')) {
      divapp.classList.remove('h-molstar');
      divapp.classList.add('h-molstar-nopanel');
    } else {
      divapp.classList.remove('h-molstar-nopanel');
      divapp.classList.add('h-molstar');
    }
  })
}


// Function to reformat the PyMOL selection into Mol* format
const reformatSelection = (selectionString) => {
  // Regular expression to extract the chain and the residue ranges
  const regex = /chain\s([A-Za-z0-9]+)\sand\sresi\s([\d\-\+\,]+)/g;
  const matches = [...selectionString.matchAll(regex)];
  if (matches.length > 0) {
    const formattedSelections = matches.flatMap(match => {
      const chain = match[1]; // Capture the chain name
      const residues = match[2]; // Capture residue ranges
      return residues.split('+').map(range => `${chain} ${range}`);
    });

    return formattedSelections.join(', ');
  } else {
    console.error("Invalid selection string format.");
    return null;
  }
};

export const applySelectionToMolstar = (selectionString, plugin) => {
  const query = compileIdListSelection(reformatSelection(selectionString), 'auth');
  try {
    if (plugin && plugin.managers) {
      plugin.managers.structure.selection.fromCompiledQuery('set',query);
    } else {
      console.error("Mol* plugin is not properly initialized.");
    }
  } catch (error) {
    console.error("Error applying selection:", error);
  }
};

export const clearSelectionToMolstar = (plugin) => {
  try {
    if (plugin && plugin.managers) {
      plugin.managers.structure.selection.clear();
    } else {
      console.error("Mol* plugin is not properly initialized.");
    }
  } catch (error) {
    console.error("Error clear selection:", error);
  }  
};

export const createPlugin = (parent) => {
  const MySpec = {
    ...DefaultPluginUISpec(),
    layout: {
      initial: {
        isExpanded: false,
        showControls: true
      },
    }, components: {
      controls: { right: React.ComponentClass, left: 'none', bottom: 'none', top: React.ComponentClass },
      remoteState: 'none'
    }, canvas3d: {
      camera: {
        helper: { axes: { name: 'off', params: {} } }
      }
    }, config: [
      [PluginConfig.Viewport.ShowExpand, true],
      [PluginConfig.Viewport.ShowControls, true],
      [PluginConfig.Viewport.ShowSelectionMode, true],
      [PluginConfig.Viewport.ShowAnimation, true],
      [PluginConfig.Viewport.ShowSettings, true],
      [PluginConfig.Viewport.ShowExpand, true],
    ]
  };
  const plugin = createPluginUI(parent, MySpec);
  sessionStorage.setItem('isLoaded', false);
  return plugin;
};

